import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import {
  auth,
  db,
  googleProvider,
  isFirebaseEnabled,
  type DbProfile,
} from "../lib/firebase";
import { userDocToProfile } from "../lib/mappers";
import { api } from "../lib/api";

// Effective UI roles. Maps from `account_type` claim, plus the implicit
// `platform_admin` claim. Kept stable across the app even as backend
// terminology evolves.
export type EffectiveRole =
  | "admin"
  | "institution"
  | "enterprise_owner"
  | "enterprise_member"
  | "individual"
  | null;

export type Permissions = {
  canModerate: boolean;
  canManageUsers: boolean;
  canCreateOrg: boolean;
  canViewGlobalSales: boolean;
  canSeeAllSkills: boolean;
  canCreatePublicSkill: boolean;
  canSubmitInstitutionSkill: boolean;
  canCreatePrivateEnterpriseSkill: boolean;
  canEditAnySkill: boolean;
  canEditOwnSkill: boolean;
  canManageEnterpriseMembers: boolean;
  canViewEnterprisePortal: boolean;
  canViewEnterpriseUsage: boolean;
  canBrowsePublicStore: boolean;
  canManageBilling: boolean;
};

// Custom claims set by the `syncUserClaims` Firestore trigger from the user
// doc. The user doc is the source of truth; these claims are derived.
type ClaimsShape = {
  platform_admin?: true;
  account_type?: "individual" | "institution" | "enterprise_owner" | "enterprise_member" | "platform_admin";
  enterprise_id?: string;
  institution_id?: string;
};

type AuthState = {
  user: User | null;
  profile: DbProfile | null;
  claims: ClaimsShape;
  _baseRole: EffectiveRole;
  _baseEnterpriseId: string | null;
  _baseInstitutionId: string | null;
  loading: boolean;
  authError: string | null;
};

export type Impersonation =
  | { kind: "enterprise"; id: string; name: string }
  | { kind: "institution"; id: string; name: string }
  | null;

export type AuthContextValue = {
  user: User | null;
  profile: DbProfile | null;
  effectiveRole: EffectiveRole;
  enterpriseId: string | null;
  institutionId: string | null;
  loading: boolean;
  authError: string | null;
  isAdmin: boolean;
  isRealAdmin: boolean;
  impersonation: Impersonation;
  permissions: Permissions;
  startImpersonation: (value: Impersonation) => void;
  stopImpersonation: () => void;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshClaims: () => Promise<void>;
};

const IMPERSONATION_KEY = "cowboy:admin:impersonation";

function loadImpersonation(): Impersonation {
  try {
    const raw = localStorage.getItem(IMPERSONATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      (parsed.kind === "enterprise" || parsed.kind === "institution") &&
      typeof parsed.id === "string" &&
      typeof parsed.name === "string"
    )
      return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

const EMPTY_PERMS: Permissions = {
  canModerate: false,
  canManageUsers: false,
  canCreateOrg: false,
  canViewGlobalSales: false,
  canSeeAllSkills: false,
  canCreatePublicSkill: false,
  canSubmitInstitutionSkill: false,
  canCreatePrivateEnterpriseSkill: false,
  canEditAnySkill: false,
  canEditOwnSkill: false,
  canManageEnterpriseMembers: false,
  canViewEnterprisePortal: false,
  canViewEnterpriseUsage: false,
  canBrowsePublicStore: false,
  canManageBilling: false,
};

function computeRole(claims: ClaimsShape): EffectiveRole {
  if (claims.platform_admin === true) return "admin";
  switch (claims.account_type) {
    case "platform_admin":
      return "admin";
    case "enterprise_owner":
      return "enterprise_owner";
    case "enterprise_member":
      return "enterprise_member";
    case "institution":
      return "institution";
    case "individual":
      return "individual";
    default:
      return null;
  }
}

function computePermissions(role: EffectiveRole): Permissions {
  switch (role) {
    case "admin":
      return {
        canModerate: true,
        canManageUsers: true,
        canCreateOrg: true,
        canViewGlobalSales: true,
        canSeeAllSkills: true,
        canCreatePublicSkill: true,
        canSubmitInstitutionSkill: true,
        canCreatePrivateEnterpriseSkill: true,
        canEditAnySkill: true,
        canEditOwnSkill: true,
        canManageEnterpriseMembers: true,
        canViewEnterprisePortal: true,
        canViewEnterpriseUsage: true,
        canBrowsePublicStore: true,
        canManageBilling: true,
      };
    case "institution":
      return {
        ...EMPTY_PERMS,
        canSubmitInstitutionSkill: true,
        canEditOwnSkill: true,
        canBrowsePublicStore: true,
      };
    case "enterprise_owner":
      return {
        ...EMPTY_PERMS,
        canCreatePrivateEnterpriseSkill: true,
        canEditOwnSkill: true,
        canManageEnterpriseMembers: true,
        canViewEnterprisePortal: true,
        canViewEnterpriseUsage: true,
        canManageBilling: true,
        canBrowsePublicStore: true,
      };
    case "enterprise_member":
      return {
        ...EMPTY_PERMS,
        canViewEnterprisePortal: true,
        canBrowsePublicStore: true,
      };
    case "individual":
      return { ...EMPTY_PERMS, canBrowsePublicStore: true };
    default:
      return EMPTY_PERMS;
  }
}

// Returns true when the caller already has a /users/{uid} doc OR has the
// platform_admin custom claim. Provisioning never happens implicitly —
// only via /accept?token=... → POST /auth/initialize with a valid token.
async function fetchMe(): Promise<{
  hasProfile: boolean;
  isPlatformAdmin: boolean;
}> {
  const me = await api.get<{
    auth: { isPlatformAdmin: boolean };
    profile: unknown | null;
  }>("/auth/me");
  return {
    hasProfile: me.profile != null,
    isPlatformAdmin: !!me.auth?.isPlatformAdmin,
  };
}

async function destroyGhostAuthUser(): Promise<void> {
  try {
    await api.post("/auth/destroy-self", {});
  } catch {
    /* ignore — best-effort cleanup */
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    claims: {},
    _baseRole: null,
    _baseEnterpriseId: null,
    _baseInstitutionId: null,
    loading: isFirebaseEnabled,
    authError: null,
  });

  const [impersonation, setImpersonationState] = useState<Impersonation>(
    () => loadImpersonation(),
  );

  function startImpersonation(value: Impersonation) {
    if (value) localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(value));
    else localStorage.removeItem(IMPERSONATION_KEY);
    setImpersonationState(value);
  }

  function stopImpersonation() {
    localStorage.removeItem(IMPERSONATION_KEY);
    setImpersonationState(null);
  }

  useEffect(() => {
    if (!auth || !db) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    let unsubProfile: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }
      if (!user) {
        setState({
          user: null,
          profile: null,
          claims: {},
          _baseRole: null,
          _baseEnterpriseId: null,
          _baseInstitutionId: null,
          loading: false,
          authError: null,
        });
        return;
      }

      // The /accept route handles its own provisioning via the invite
      // token, so we skip the ghost-user check there. Anywhere else, a
      // signed-in user without a profile (and no admin claim) is rejected
      // and their Auth record is wiped server-side.
      const onAcceptRoute =
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/accept");
      if (!onAcceptRoute) {
        try {
          const me = await fetchMe();
          if (!me.hasProfile && !me.isPlatformAdmin) {
            await destroyGhostAuthUser();
            try {
              await firebaseSignOut(auth!);
            } catch {
              /* ignore */
            }
            setState({
              user: null,
              profile: null,
              claims: {},
              _baseRole: null,
              _baseEnterpriseId: null,
              _baseInstitutionId: null,
              loading: false,
              authError:
                "Esta cuenta no está autorizada. Pide una invitación al administrador.",
            });
            return;
          }
        } catch (e) {
          const message =
            e instanceof Error ? e.message : "Sign-in failed.";
          try {
            await firebaseSignOut(auth!);
          } catch {
            /* ignore */
          }
          setState({
            user: null,
            profile: null,
            claims: {},
            _baseRole: null,
            _baseEnterpriseId: null,
            _baseInstitutionId: null,
            loading: false,
            authError: message,
          });
          return;
        }
      }

      // Force token refresh so the freshly synced claims arrive on the client.
      const token = await user.getIdTokenResult(true);
      const claims: ClaimsShape = {
        platform_admin: token.claims.platform_admin === true ? true : undefined,
        account_type:
          typeof token.claims.account_type === "string"
            ? (token.claims.account_type as ClaimsShape["account_type"])
            : undefined,
        enterprise_id:
          typeof token.claims.enterprise_id === "string"
            ? (token.claims.enterprise_id as string)
            : undefined,
        institution_id:
          typeof token.claims.institution_id === "string"
            ? (token.claims.institution_id as string)
            : undefined,
      };

      unsubProfile = onSnapshot(doc(db!, "users", user.uid), (snap) => {
        const profile = snap.exists()
          ? userDocToProfile(user.uid, snap.data())
          : null;
        setState({
          user,
          profile,
          claims,
          _baseRole: computeRole(claims),
          _baseEnterpriseId: claims.enterprise_id ?? null,
          _baseInstitutionId: claims.institution_id ?? null,
          loading: false,
          authError: null,
        });
      });
    });

    return () => {
      unsubAuth();
      if (unsubProfile) unsubProfile();
    };
  }, []);

  async function signInWithGoogle() {
    if (!auth) {
      throw new Error(
        "Firebase no está configurado. Crea app/.env.local con la config del proyecto.",
      );
    }
    setState((s) => ({ ...s, authError: null }));
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (
        code === "auth/popup-blocked" ||
        code === "auth/operation-not-supported-in-this-environment"
      ) {
        await signInWithRedirect(auth, googleProvider);
      } else if (code !== "auth/popup-closed-by-user") {
        throw err;
      }
    }
  }

  async function signOut() {
    if (!auth) return;
    await firebaseSignOut(auth);
  }

  // Forces Firebase to re-issue the ID token AND mirrors the new claims
  // into local React state. Just calling getIdToken(true) on its own does
  // not update useAuth's state, so the UI keeps the old (often empty)
  // claims and computes the wrong role. Called right after the invite
  // redeem so the user lands on the right dashboard without a hard reload.
  async function refreshClaims() {
    if (!auth?.currentUser) return;
    const token = await auth.currentUser.getIdTokenResult(true);
    const claims: ClaimsShape = {
      platform_admin: token.claims.platform_admin === true ? true : undefined,
      account_type:
        typeof token.claims.account_type === "string"
          ? (token.claims.account_type as ClaimsShape["account_type"])
          : undefined,
      enterprise_id:
        typeof token.claims.enterprise_id === "string"
          ? (token.claims.enterprise_id as string)
          : undefined,
      institution_id:
        typeof token.claims.institution_id === "string"
          ? (token.claims.institution_id as string)
          : undefined,
    };
    setState((s) => ({
      ...s,
      claims,
      _baseRole: computeRole(claims),
      _baseEnterpriseId: claims.enterprise_id ?? null,
      _baseInstitutionId: claims.institution_id ?? null,
    }));
  }

  const isRealAdmin = state.claims.platform_admin === true;
  const activeImpersonation: Impersonation = isRealAdmin ? impersonation : null;

  const effectiveRole: EffectiveRole = activeImpersonation
    ? activeImpersonation.kind === "enterprise"
      ? "enterprise_owner"
      : "institution"
    : state._baseRole;

  const enterpriseId: string | null =
    activeImpersonation?.kind === "enterprise"
      ? activeImpersonation.id
      : state._baseEnterpriseId;

  const institutionId: string | null =
    activeImpersonation?.kind === "institution"
      ? activeImpersonation.id
      : state._baseInstitutionId;

  const permissions = useMemo(
    () => computePermissions(effectiveRole),
    [effectiveRole],
  );

  const value: AuthContextValue = {
    user: state.user,
    profile: state.profile,
    effectiveRole,
    enterpriseId,
    institutionId,
    loading: state.loading,
    authError: state.authError,
    isAdmin: effectiveRole === "admin",
    isRealAdmin,
    impersonation: activeImpersonation,
    permissions,
    startImpersonation,
    stopImpersonation,
    signInWithGoogle,
    signOut,
    refreshClaims,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
